import React, { useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import './styles/animations.css'
import { Splash } from './components/screens/Splash.js'
import { TheaterSelect } from './components/screens/TheaterSelect.js'
import { Placement } from './components/screens/Placement.js'
import { Battle } from './components/screens/Battle.js'
import { Results } from './components/screens/Results.js'
import { Stats } from './components/screens/Stats.js'
import { useSocket } from './hooks/useSocket.js'
import { useGame } from './hooks/useGame.js'
import { useGameStore } from './store/gameStore.js'
import { useStatsStore } from './store/statsStore.js'
import { useAnimationStore } from './store/animationStore.js'
import { connectSocket, disconnectSocket } from './socket/client.js'
import { trackScreen } from './utils/analytics.js'
import type { Ship } from '@fleetduel/shared'

function App() {
  useSocket()

  const phase = useGameStore(s => s.phase)
  const roomId = useGameStore(s => s.roomId)
  const reset = useGameStore(s => s.reset)
  const { placeShips, createSinglePlayer } = useGame()
  const [showStats, setShowStats] = useState(false)
  // Pre-game menu flow: menu → theater select → game (GDD-v3 §2).
  // Difficulty adapts automatically: the game starts at the highest tier
  // unlocked by the player's win record, so there is no difficulty screen.
  const [menuScreen, setMenuScreen] = useState<'menu' | 'theater'>('menu')

  useEffect(() => {
    connectSocket()
    return () => disconnectSocket()
  }, [])

  // Analytics: virtual page view per screen (mirrors the render routing below)
  const screen = showStats
    ? 'stats'
    : !roomId
      ? (menuScreen === 'menu' ? 'splash' : 'theater_select')
      : phase === 'SETUP'
        ? 'placement'
        : phase === 'IN_PROGRESS'
          ? 'battle'
          : 'results'

  useEffect(() => {
    trackScreen(screen)
  }, [screen])

  const handleStart = useCallback(() => {
    const unlocked = useStatsStore.getState().unlockedDifficulties
    const difficulty = unlocked.has('hard') ? 'hard' : unlocked.has('medium') ? 'medium' : 'easy'
    createSinglePlayer(difficulty)
  }, [createSinglePlayer])

  const handlePlacement = useCallback((ships: Ship[]) => {
    placeShips(ships)
  }, [placeShips])

  const handleReplay = useCallback(() => {
    useAnimationStore.getState().resetAnimations()
    setMenuScreen('menu')
    reset()
  }, [reset])

  // Stats screen (accessible from splash)
  if (showStats) {
    return <Stats onBack={() => setShowStats(false)} />
  }

  // No room yet — pre-game menu flow
  if (!roomId) {
    switch (menuScreen) {
      case 'menu':
        return <Splash onPlay={() => setMenuScreen('theater')} />
      case 'theater':
        return (
          <TheaterSelect
            onStart={handleStart}
            onBack={() => setMenuScreen('menu')}
          />
        )
    }
  }

  // Route based on game phase
  switch (phase) {
    case 'SETUP':
      return <Placement onConfirm={handlePlacement} />
    case 'IN_PROGRESS':
      return <Battle />
    case 'GAME_OVER':
      // Keep the battlefield mounted behind the results overlay
      return (
        <>
          <Battle />
          <Results onReplay={handleReplay} />
        </>
      )
  }
}

// Global styles
const style = document.createElement('style')
style.textContent = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body {
    /* No pull-to-refresh / rubber-banding during play */
    overscroll-behavior: none;
  }
  body {
    background-color: #0B2545;
    color: #EAF2F8;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    -webkit-font-smoothing: antialiased;
    min-width: 320px;
    /* Kill the 300ms double-tap-zoom delay and grey tap flash on mobile */
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
  button { font-family: inherit; touch-action: manipulation; }
  /* Full-height screens: dvh tracks the mobile browser's collapsing URL bar */
  .screen-fill {
    min-height: 100vh;
    min-height: 100dvh;
  }
`
document.head.appendChild(style)

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
